import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FiClock, FiArrowRight, FiCheck } from 'react-icons/fi';
import { Link, useNavigate } from 'react-router-dom';
import { getServices } from '../../services/api';
import './Services.css';

const CATEGORY_HIGHLIGHTS = {
  PSICOTERAPIA: {
    headline: 'Procesos terapéuticos centrados en ti',
    description:
      'Psicólogos clínicos certificados trabajan contigo en sesiones presenciales y online para acompañar cada etapa de tu bienestar emocional.'
  },
  'TERAPIA OCUPACIONAL': {
    headline: 'Autonomía e integración en la vida cotidiana',
    description:
      'Evaluaciones y tratamientos personalizados para potenciar habilidades sensoriales, motoras y socioemocionales en niños, jóvenes y adultos.'
  },
  PSICOPEDAGOGÍA: {
    headline: 'Acompañamiento educativo y estrategias de aprendizaje',
    description:
      'Diagnósticos e intervenciones que fortalecen los procesos cognitivos y emocionales en el contexto escolar y familiar.'
  },
  PSIQUIATRÍA: {
    headline: 'Diagnóstico médico y farmacoterapia segura',
    description:
      'Equipo médico que integra neurociencias, control farmacológico y coordinación permanente con el equipo terapéutico.'
  }
};

const EXCLUDED_CATEGORIES = ['Cursos y Talleres'];
const EXCLUDED_KEYWORDS = ['arriendo', 'box', 'salon'];

const formatCurrency = (value) =>
  new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 }).format(
    Number(value) || 0
  );

const normalizeCategory = (raw) => {
  if (!raw) return { id: 'OTROS', label: 'Otros Servicios' };
  const trimmed = String(raw).trim();
  const id = trimmed.toUpperCase();
  const label = trimmed
    .toLowerCase()
    .split(/\s+/)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
  return { id, label };
};

const desiredOrder = ['PSICOTERAPIA', 'TERAPIA OCUPACIONAL', 'PSICOPEDAGOGÍA', 'PSIQUIATRÍA'];

const Services = () => {
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeCategoryId, setActiveCategoryId] = useState('');

  const fetchServices = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await getServices();
      if (!Array.isArray(data)) {
        setServices([]);
        setError('Formato de datos inválido recibido del servidor.');
        return;
      }

      const filtered = data.filter((service) => {
        const name = (service?.nombre || '').toLowerCase();
        const category = (service?.categoria || '').toLowerCase();
        if (EXCLUDED_CATEGORIES.map((c) => c.toLowerCase()).includes(category)) return false;
        return !EXCLUDED_KEYWORDS.some((keyword) => name.includes(keyword) || category.includes(keyword));
      });

      setServices(filtered);
    } catch (err) {
      const message = err?.response
        ? `Error del servidor (${err.response.status}). Intenta nuevamente más tarde.`
        : 'No pudimos conectar con el servidor. Asegúrate de que el backend esté en ejecución.';
      setError(message);
      setServices([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchServices();
  }, [fetchServices]);

  const servicesByCategory = useMemo(() => {
    const grouped = {};
    services.forEach((service) => {
      const { id, label } = normalizeCategory(service.categoria);
      if (!grouped[id]) {
        grouped[id] = { label, items: [] };
      }
      grouped[id].items.push(service);
    });
    return grouped;
  }, [services]);

  const categories = useMemo(() => {
    const entries = Object.entries(servicesByCategory).map(([id, value]) => ({ id, label: value.label }));
    return entries.sort((a, b) => {
      const indexA = desiredOrder.indexOf(a.id);
      const indexB = desiredOrder.indexOf(b.id);
      if (indexA === -1 && indexB === -1) return a.label.localeCompare(b.label);
      if (indexA === -1) return 1;
      if (indexB === -1) return -1;
      return indexA - indexB;
    });
  }, [servicesByCategory]);

  useEffect(() => {
    if (!activeCategoryId && categories.length > 0) {
      setActiveCategoryId(categories[0].id);
    }
  }, [activeCategoryId, categories]);

  const activeCategory = useMemo(() => {
    if (!activeCategoryId || !servicesByCategory[activeCategoryId]) return null;
    return servicesByCategory[activeCategoryId];
  }, [activeCategoryId, servicesByCategory]);

  const handleReserve = (service) => {
    const selectedService = {
      id: service.id,
      nombre: service.nombre,
      precio: service.precio,
      duracion: service.duracion,
      categoria: activeCategory?.label || service.categoria || 'Servicio'
    };

    navigate('/reservar/colaborador', {
      state: {
        services: [selectedService],
        total: Number(selectedService.precio) || 0
      }
    });
  };

  return (
    <section className="services">
      <div className="services-container">
        <div className="services-header">
          <span className="services-badge">Servicios especializados</span>
          <h2 className="services-title">Encuentra el espacio terapéutico ideal para ti</h2>
          <p className="services-subtitle">
            Navega por nuestras categorías y agenda sesiones reales con el equipo profesional de IntegraMente.
          </p>
        </div>

        {loading && (
          <div className="services-state">
            <p>Cargando servicios disponibles...</p>
          </div>
        )}

        {error && !loading && (
          <div className="services-state services-error">
            <p>{error}</p>
            <button type="button" onClick={fetchServices}>
              Reintentar carga
            </button>
          </div>
        )}

        {!loading && !error && categories.length > 0 && (
          <>
            <div className="services-tabs" role="tablist">
              {categories.map((category) => (
                <button
                  key={category.id}
                  className={`services-tab ${category.id === activeCategoryId ? 'active' : ''}`}
                  onClick={() => setActiveCategoryId(category.id)}
                  role="tab"
                  aria-selected={category.id === activeCategoryId}
                >
                  {category.label}
                </button>
              ))}
            </div>

            <div className="services-highlight">
              <h3>{CATEGORY_HIGHLIGHTS[activeCategoryId]?.headline || (activeCategory?.label ?? '')}</h3>
              <p>
                {CATEGORY_HIGHLIGHTS[activeCategoryId]?.description ||
                  'Explora nuestras sesiones disponibles y selecciona la que mejor se ajuste a tus objetivos.'}
              </p>
            </div>

            <div className="services-grid">
              {activeCategory?.items.map((service) => {
                const duration = service.duracion || service.duration;
                const focus =
                  service.modalidad ||
                  service.tipo ||
                  (service.categoria ? `${service.categoria.toLowerCase()}` : 'Profesionales certificados');
                const description =
                  service.descripcion ||
                  service.detalle ||
                  'Sesiones diseñadas a la medida de tus necesidades terapéuticas.';

                return (
                  <article key={service.id} className="service-card">
                    <header className="service-card-header">
                      <h4>{service.nombre}</h4>
                      <span className="service-price">{formatCurrency(service.precio)}</span>
                    </header>
                    <p className="service-summary">{description}</p>
                    <ul className="service-tags">
                      {duration && (
                        <li>
                          <FiClock />
                          {duration} min
                        </li>
                      )}
                      <li>
                        <FiCheck />
                        {focus.charAt(0).toUpperCase() + focus.slice(1)}
                      </li>
                    </ul>
                    <button className="service-cta" onClick={() => handleReserve(service)}>
                      Reservar este servicio
                      <FiArrowRight />
                    </button>
                  </article>
                );
              })}
            </div>
          </>
        )}

        {!loading && !error && categories.length === 0 && (
          <div className="services-state">
            <p>Por el momento no hay servicios disponibles para mostrar.</p>
          </div>
        )}

        <div className="services-footer">
          <Link to="/servicios" className="services-link">
            Ver todos los servicios
            <FiArrowRight />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default Services;
